import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { displayToastError } from '@/helpers';
import { useModal } from '@/hooks/use-modal';
import { IntegrationConnectionTrigger } from '@/modules/common/components/integration-connection-trigger';
import { useOAuthProcessor } from '@/modules/common/hooks/use-oauth-processor';
import { PollingInterval } from '@/modules/common/types/polling-interval';
import { OnboardingBottomSection } from '@/modules/onboarding/common/components/onboarding-bottom-section';
import { useRootSelector } from '@/router/state/root/util/context/helpers';
import { logger } from '@/utils/logger/logger';

import { IntegrationServices, IntegrationState } from '../../../common/types/integrations';
import { submit } from '../api';
import { IntegrationsTable } from '../components/integration/integrations-table';
import { NoIntegrationsConfirmationModal } from '../components/modals/no-integrations-confirmation-modal';
import { ReasonsModal } from '../components/modals/reasons-modal';
import { RequestModal } from '../components/modals/request-modal';
import { mergeIntegrationsWithStatus } from '../helpers/merge-integrations';
import { useIntegrationStatus } from '../hooks/use-integration-status';

const TAG = 'IntegrationsOnboardingStepPage:';

export const IntegrationsOnboardingStepPage: FC = () => {
  const state = useRootSelector(state => state);

  // Process OAuth callback if present in URL
  useOAuthProcessor();

  // Track which integration's connection modal is open
  const [activeConnectionService, setActiveConnectionService] =
    useState<IntegrationServices | null>(null);

  const [pollingInterval, setPollingInterval] = useState(PollingInterval.LONG);

  const { data: integrationStatusData } = useIntegrationStatus({
    enablePolling: activeConnectionService === null, // Pause polling when modal is open
    pollingInterval: pollingInterval,
  });

  const reasonsModalProps = useModal();
  const requestModalProps = useModal();
  const noIntegrationsModalProps = useModal();

  const { onboardingMachine } = state.children;
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSuccess = useCallback(() => {
    if (isMountedRef.current) {
      onboardingMachine?.send({ type: 'NEXT' });
    }
  }, [onboardingMachine]);

  const submitMutation = useMutation({
    mutationKey: ['submit-integrations'],
    mutationFn: submit,
    onSuccess: handleSuccess,
    onError: (error: Error) => {
      if (isMountedRef.current) {
        displayToastError(error.message);
      }
    },
    retry: false,
  });

  useEffect(() => {
    return () => {
      // Cancel any pending mutations and reset state on unmount
      submitMutation.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReasons = () => {
    reasonsModalProps.onOpen();
  };

  const handleConnect = (service: IntegrationServices) => {
    setActiveConnectionService(service);
  };

  const handleCloseConnectionModal = () => {
    setActiveConnectionService(null);
  };

  const handleRequest = () => {
    requestModalProps.onOpen();
  };

  const handleNext = () => {
    // Check if any integrations are connected
    const hasConnectedIntegrations = integrations.some(
      integration => integration.state === IntegrationState.CONNECTED
    );

    if (!hasConnectedIntegrations) {
      // Show confirmation modal if no integrations are connected
      noIntegrationsModalProps.onOpen();
    } else {
      // Proceed directly if integrations are connected
      submitMutation.mutate();
    }
  };

  const handleConfirmSkip = () => {
    // User confirmed to skip integrations
    noIntegrationsModalProps.onClose();
    submitMutation.mutate();
  };

  // Merge API integration status with local integration definitions
  // Preserves local order and appends any integrations from API not in local list
  const integrations = useMemo(() => {
    if (!integrationStatusData) {
      // Return local data with default state while loading
      return mergeIntegrationsWithStatus([]);
    }
    return mergeIntegrationsWithStatus(integrationStatusData.integrations);
  }, [integrationStatusData]);

  useEffect(() => {
    if (integrations.some(integration => integration.state == IntegrationState.SYNCING)) {
      logger.trace(TAG, 'Integration is syncing, setting polling interval to SHORT');
      setPollingInterval(PollingInterval.SHORT);
    } else {
      logger.trace(TAG, 'No integrations are syncing, setting polling interval to LONG');
      setPollingInterval(PollingInterval.LONG);
    }
  }, [integrations, setPollingInterval]);

  return (
    <>
      <div>
        <div className="mb-6 flex w-full flex-col">
          <div className="w-full max-w-3xl px-12 pt-8">
            <h1 className="mb-2 text-2xl font-semibold">{t('title')}</h1>

            <p className="mb-6 text-sm text-muted-foreground">{t('description')}</p>

            <div className="rounded-lg border border-input p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="mb-1.5 text-lg font-semibold leading-[1]">
                    {t('integrationSection.title')}
                  </h3>

                  <p className="text-sm/5 text-muted-foreground">
                    {t('integrationSection.description')}
                  </p>
                </div>

                <Alert variant="info">
                  <div className="flex items-center gap-1.5">
                    <Info className="h-4 w-4 flex-shrink-0" />

                    <AlertDescription className="text-xs/4">
                      {t('integrationSection.alert')}
                    </AlertDescription>
                  </div>
                </Alert>

                <IntegrationsTable
                  integrations={integrations}
                  onReasons={handleReasons}
                  onConnect={handleConnect}
                />
                <NoIntegrationsConfirmationModal
                  {...noIntegrationsModalProps}
                  onConfirm={handleConfirmSkip}
                  isLoading={submitMutation.isPending}
                />
                <ReasonsModal {...reasonsModalProps} service={IntegrationServices.GOOGLE} />

                <div className="flex items-center justify-end gap-4 px-4">
                  <p className="text-sm">{t('integrationSection.findTool')}</p>

                  <Button size="sm" onClick={handleRequest}>
                    {t('integrationSection.actions.requestIntegration')}
                  </Button>

                  <RequestModal {...requestModalProps} />
                </div>
              </div>
            </div>
          </div>

          <div className="pb-[72px]" />
        </div>

        {/* Connection modal at page level to prevent unmounting during table re-renders */}
        {activeConnectionService && (
          <IntegrationConnectionTrigger
            service={activeConnectionService}
            open={true}
            onOpenChange={open => {
              if (!open) {
                handleCloseConnectionModal();
              }
            }}
          />
        )}

        <OnboardingBottomSection
          nextButtonProps={{
            text: t('next'),
            onClick: handleNext,
            isLoading: submitMutation.isPending,
          }}
          backButtonProps={{
            text: t('back'),
            onClick: () => onboardingMachine?.send({ type: 'BACK' }),
          }}
        />
      </div>
    </>
  );
};
